import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import TripReport from '../components/trip/TripReport.jsx';

/**
 * TripReportPage — /trip/report/:id inside Classify.
 * Loads a previously-computed (tenant-scoped) TRIP report and renders it.
 * PDF export goes through the authenticated api helper (blob) so it carries
 * the Firebase token to the now-protected export route.
 */
export default function TripReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/trip/report/${id}`);
        if (!cancelled) setReport(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Could not load this TRIP report.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  async function exportPDF() {
    setExporting(true);
    try {
      const res = await api.get(`/trip/export/pdf/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const name = (report?.input?.business_name || report?.input?.address || 'trip_report')
        .replace(/[^a-z0-9]/gi, '_').slice(0, 60);
      link.download = `understudy_trip_${name}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('TRIP PDF export failed:', err);
      toast.error('PDF export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <Loader2 className="animate-spin text-teal mx-auto mb-4" size={40} />
        <div className="text-gray-500">Loading TRIP report…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <div className="font-semibold text-red-700 mb-1">Unable to load report</div>
            <div className="text-red-600">{error}</div>
          </div>
        </div>
        <button
          onClick={() => navigate('/trip')}
          className="mt-6 text-teal hover:text-teal-dark text-sm font-semibold flex items-center gap-2"
        >
          <ArrowLeft size={14} /> Back to TRIP search
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
        <button
          onClick={() => navigate('/trip')}
          className="text-teal hover:text-teal-dark text-sm font-semibold flex items-center gap-2"
        >
          <ArrowLeft size={14} /> Run another address
        </button>
        <button
          onClick={exportPDF}
          disabled={exporting}
          className="text-sm font-semibold flex items-center gap-2 border border-teal text-teal hover:bg-teal hover:text-white transition px-4 py-2 rounded-lg disabled:opacity-60"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Export PDF
        </button>
      </div>
      <TripReport report={report} />
    </div>
  );
}
